import { Controller, Form, FormProvider } from 'react-hook-form';
import { isNil } from 'lodash';
import { t } from 'i18next';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';

import { USER_SUFFIX } from '@common/types';
import {
  CustomModal,
  ProviderSearchFormField,
  SaveChangesModal,
  SearchableSelect,
} from '@components';

import { useCreateProviderModal } from './hooks';
import { GuideCertifiedSection } from './components/form';
import { ContactInformationSection } from './components/form/ContactInformationSection';
import { Select } from 'ui';
import { theme } from '@store';

export const CreateProviderModal = ({
  isOpen,
  handleClose,
  handleOpenProviderDetails,
}: {
  isOpen: boolean;
  handleClose: () => void;
  handleOpenProviderDetails: (id: string) => void;
}) => {
  const {
    formMethods,
    onCloseAction,
    onSubmit,
    partnerOptions,
    practiceOptions,
    providerSpecialtyOptions,
    setValue,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
    handleChangeRegionQuery,
    providerRegionOptions,
    regionSearchQuery,
    isRegionOptionsLoading,
    clinicalSearchQuery,
    isClinicalOptionsLoading,
    handleChangeClinicalQuery,
    providerClinicalOptions,
    // handleChangePhysicianQuery,
    // physicianSearchQuery,
    // isPhysicianOptionsLoading,
    // providerPhysicianOptions
  } = useCreateProviderModal(isOpen, handleClose, handleOpenProviderDetails);

  const {
    control,
    formState: { isSubmitting },
    watch,
  } = formMethods;

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={onCloseAction}
        title={t('adminPanel.providers.addModal.title')}
        isLoading={isSubmitting}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='first_name'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    size='medium'
                    label={t('adminPanel.providers.addModal.firstName')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
              <Controller
                name='last_name'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    type='text'
                    size='medium'
                    label={t('adminPanel.providers.addModal.lastName')}
                    variant='outlined'
                    error={!isNil(error)}
                    helperText={error ? error.message : null}
                    required
                    fullWidth
                  />
                )}
              />
            </Box>

            <Controller
              name='suffix'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t('adminPanel.providers.addModal.optionalSuffix')}
                  error={!isNil(error)}
                  options={Object.values(USER_SUFFIX).map(o => ({
                    value: o,
                    label: o,
                  }))}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name='clinical_leader_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <SearchableSelect
                  {...field}
                  label={t('adminPanel.providers.addModal.clinical_leader_id')}
                  error={!isNil(error)}
                  onChange={value => field.onChange(value)}
                  options={providerClinicalOptions}
                  helperText={error?.message}
                  searchQuery={clinicalSearchQuery}
                  onChangeSearchQuery={handleChangeClinicalQuery}
                  isLoading={isClinicalOptionsLoading}
                />
              )}
            />

            <ProviderSearchFormField
              name='supervising_physician_id'
              placeholder={t(
                'patient.careTeam.modal.labels.supervising_physician_id',
              )}
              optional
            />

            <Controller
              name='external'
              control={control}
              defaultValue={false}
              render={({ field: { onChange, value } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={(_, checked) => onChange(checked)}
                    />
                  }
                  label={t('adminPanel.providers.addModal.external')}
                />
              )}
            />

            <Controller
              name='npi'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  size='medium'
                  label={t('adminPanel.providers.addModal.licenses.npi')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='provider_specialty'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  size='medium'
                  label={t(
                    'adminPanel.providers.addModal.providerSpecialtyLabel',
                  )}
                  error={!isNil(error)}
                  options={providerSpecialtyOptions}
                  helperText={error?.message}
                  required
                />
              )}
            />

            <Divider sx={{ width: '100%' }} />
            <GuideCertifiedSection />
            <Divider sx={{ width: '100%' }} />

            <Controller
              name='region_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <SearchableSelect
                  {...field}
                  label={t('adminPanel.providers.addModal.region')}
                  error={!isNil(error)}
                  onChange={value => field.onChange(value)}
                  options={providerRegionOptions}
                  helperText={error?.message}
                  searchQuery={regionSearchQuery}
                  onChangeSearchQuery={handleChangeRegionQuery}
                  isLoading={isRegionOptionsLoading}
                />
              )}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'row',
                gap: '0.5rem',
              }}
            >
              <Controller
                name='partner_id'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    size='medium'
                    label={t('adminPanel.providers.addModal.partner')}
                    error={!isNil(error)}
                    required
                    onChange={event => {
                      field.onChange(event);
                      setValue('practice_id', '');
                    }}
                    options={partnerOptions}
                    helperText={error?.message}
                  />
                )}
              />
              <Controller
                name='practice_id'
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Select
                    {...field}
                    size='medium'
                    label={t('adminPanel.providers.addModal.practice')}
                    error={!isNil(error)}
                    required
                    disabled={!watch('partner_id')}
                    options={practiceOptions}
                    helperText={error?.message}
                  />
                )}
              />
            </Box>

            <ContactInformationSection />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                paddingY: 3,
                backgroundColor: theme.palette.common.white,
                zIndex: 3,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isSubmitting}
                onClick={onCloseAction}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isSubmitting}
              >
                {t('general.ADD')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
